"use client";

import React, {
    forwardRef,
    useMemo,
    useState,
    useImperativeHandle,
    useEffect,
    useRef,
} from "react";
import {
    Stack,
    Text,
    Dropdown,
    IDropdownOption,
    IconButton,
    Icon,
} from "@fluentui/react";
import { toFixedWithoutRounding } from "../../../app/Helpers";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../app/Hooks";
import { useTranslation } from "react-i18next";
import { OrderAction } from "../../../app/Enums";
import { Order } from "../../../repository/Trading/order_pb";
import Decimal from "decimal.js";

// -----------------------------
// Types & Interfaces
// -----------------------------
export interface OrderBookEntry {
    price: any;
    amount: any;
    total: any
}

export interface DepthSnapshot {
    bids: OrderBookEntry[];
    asks: OrderBookEntry[];
    lastPrice?: any;
}



export interface OrderBookHandle {
    init(currencyPair: any): void;
    update(snapshot: DepthSnapshot): void;
    add(side: OrderAction, entry: OrderBookEntry): void;
    remove(side: OrderAction, entry: OrderBookEntry): void;
    clear(): void;
    getPair(): any;
    setPrice(lastPrice: any): void;
}

interface Props {
    depth?: number;
}

// -----------------------------
// Helpers
// -----------------------------
/** Create some placeholder rows so the component isn't empty */


/**
 * Bucket raw price levels into tick‑size groups, summing quantities.
 * For bids we floor to the bucket, for asks we ceil so that the bucket
 * price is always inside the book side.
 */
function groupByTick(
    rows: OrderBookEntry[],
    tick: number,
    side: "bid" | "ask",
    currencyPair: any,
): OrderBookEntry[] {
    if (!tick) return rows;
    const map = new Map<any, any>(); // bucketPrice -> qty sum
    rows.forEach(({ price, amount, total }) => {

        const multiplier = Math.round(1 / tick);
        let bucket = side === "bid" ? ((price.mul(multiplier).floor()).dividedBy(multiplier)) : ((price.mul(multiplier).ceil()).dividedBy(multiplier));
        let b = toFixedWithoutRounding(bucket.toString(), currencyPair?.quoteCurrencyDecimalPlaces ?? 9)
        map.set(b, { amount: (map.get((b))?.amount ?? Decimal(0)).add(amount), total: (map.get((b))?.total ?? Decimal(0)).add(total) });

    });

    const aggregated: OrderBookEntry[] = Array.from(map.entries()).map(
        ([price, a]) => ({ price: new Decimal(price), ...a })
    );
    // sort so highest price first for both sides (matches Binance collapsed view)
    aggregated.sort((a, b) => b.price.sub(a.price).toNumber());
    return aggregated;
}
function generateOptions(decimalPlace: number) {
    const result = [];

    for (let i = 1; i <= decimalPlace; i++) {
        const value = (1 / Math.pow(10, i)).toFixed(i); // e.g., 0.1, 0.01, 0.001
        result.push({ key: value, text: value });
    }

    if (decimalPlace === 0) {
        result.push({ key: "1", text: "1" });
    }

    // Reverse to get highest values first, then take top 3
    return result.reverse().slice(0, 3);
}

// -----------------------------
// Component
// -----------------------------
const BinanceOrderBook = forwardRef<OrderBookHandle, Props>(({ depth = 10 }, ref) => {
    // ─── State ────────────────────────────────────────────────────
    const navigate = useNavigate();
    const dispatch = useAppDispatch()
    const { t, i18n } = useTranslation();

    const [bids, setBids] = useState<OrderBookEntry[]>([]);
    const [asks, setAsks] = useState<OrderBookEntry[]>([]);
    const [lastPrice, setLastPrice] = useState<Decimal>(Decimal(0));
    const [prevPrice, setPrevPrice] = useState<Decimal>(Decimal(0));
    const [markPrice, setMarkPrice] = useState<Decimal>(Decimal(0));
    const [tickSize, setTickSize] = useState<string>("");
    const [currencyPair, setCurrencyPair] = useState<any>(undefined);

    const [layout, setLayout] = useState<"both" | "bid" | "ask">("both");
    const itemRef = useRef<HTMLDivElement>(null);
    // ─── Imperative API ───────────────────────────────────────────
    useImperativeHandle(
        ref,
        () => ({
            init: (c: any) => {
                if (currencyPair && currencyPair.id == c.id)
                    return;
                setCurrencyPair(c)
                setTickSize((1 / Math.pow(10, c?.quoteCurrencyDecimalPlaces)).toFixed(c?.quoteCurrencyDecimalPlaces))
            },
            getPair: () => {
                return currencyPair
            },
            setPrice: (lp: any) => {
                if (lp !== undefined) {
                    setPrevPrice(lastPrice);
                    setLastPrice(Decimal(lp));
                }
            },
            update: ({ bids: b, asks: a, lastPrice: lp }) => {
                setBids(
                    b.map(e => ({
                        price: new Decimal(e.price),
                        amount: new Decimal(e.amount),
                        total: new Decimal(e.total),
                    }))
                );

                setAsks(
                    a.map(e => ({
                        price: new Decimal(e.price),
                        amount: new Decimal(e.amount),
                        total: new Decimal(e.total),
                    }))
                );
                if (lp !== undefined) {
                    setPrevPrice(lastPrice);
                    setLastPrice(Decimal(lp));
                }
            },
            add: (side: OrderAction, e: OrderBookEntry) => {
                //setCurrencyPair(e.currencyPair)
                if (side == OrderAction.BUY) {
                    setBids(x => [...x, {
                        price: new Decimal(e.price),
                        amount: new Decimal(e.amount),
                        total: new Decimal(e.total),
                    }]);
                } else {
                    setAsks(x => [...x, {
                        price: new Decimal(e.price),
                        amount: new Decimal(e.amount),
                        total: new Decimal(e.total),
                    }]);
                }


            },
            remove: (side: OrderAction, e: OrderBookEntry) => {
                //setCurrencyPair(e.currencyPair)
                e.amount = Decimal(e.amount)
                e.price = Decimal(e.price)
                e.total = Decimal(e.total)
                if (side == OrderAction.BUY) {

                    setBids(x => {
                        var a = groupByTick(x, numericTick, "bid", currencyPair)
                        a = a.filter(c => c.price.equals(e.price))
                        if (a.length > 0) {
                            if (a[0].amount.sub(e.amount).lessThanOrEqualTo(Decimal(0))) {
                                return x.filter(c => !c.price.equals(e.price));
                            } else {
                                return x.map(c => {
                                    if (c.price.equals(e.price)) {
                                        const newAmount = c.amount.sub(e.amount);
                                        const newTotal = c.total.sub(e.total);
                                        return {
                                            ...c,
                                            amount: newAmount,
                                            total: newTotal
                                        };
                                    }

                                    return c;
                                });

                            }
                        }
                        return x;

                    });
                } else if (side == OrderAction.SELL) {
                    setAsks(x => {
                        var a = groupByTick(x, numericTick, "ask", currencyPair)
                        a = a.filter(c => c.price.equals(e.price))
                        if (a.length > 0) {
                            if (a[0].amount.sub(e.amount).lessThanOrEqualTo(Decimal(0))) {
                                return x.filter(c => !c.price.equals(e.price));
                            } else {
                                return x.map(c => {
                                    if (c.price.equals(e.price)) {
                                        const newAmount = c.amount.sub(e.amount);
                                        const newTotal = c.total.sub(e.total);
                                        return {
                                            ...c,
                                            amount: newAmount,
                                            total: newTotal
                                        };
                                    }
                                    return c;
                                });


                            }
                        }
                        return x;
                    });
                } else {
                    setBids(x => {
                        var a = groupByTick(x, numericTick, "bid", currencyPair)
                        a = a.filter(c => c.price.equals(e.price))
                        if (a.length > 0) {
                            if (a[0].amount.sub(e.amount).lessThanOrEqualTo(Decimal(0))) {
                                return x.filter(c => !c.price.equals(e.price));

                            } else {
                                return x.map(c => {
                                    if (c.price.equals(e.price)) {
                                        const newAmount = c.amount.sub(e.amount);
                                        const newTotal = c.total.sub(e.total);
                                        return {
                                            ...c,
                                            amount: newAmount,
                                            total: newTotal
                                        };
                                    }
                                    return c;
                                });



                            }
                        }
                        return x;
                    });
                    setAsks(x => {
                        var a = groupByTick(x, numericTick, "ask", currencyPair)
                        a = a.filter(c => c.price.equals(e.price))

                        if (a.length > 0) {
                            if (a[0].amount.sub(e.amount).lessThanOrEqualTo(Decimal(0))) {
                                return x.filter(c => !c.price.equals(e.price));

                            } else {
                                return x.map(c => {
                                    if (c.price.equals(e.price)) {
                                        const newAmount = c.amount.sub(e.amount);
                                        const newTotal = c.total.sub(e.total);
                                        return {
                                            ...c,
                                            amount: newAmount,
                                            total: newTotal
                                        };
                                    }
                                    return c;
                                });

                            }
                        }
                        return x;
                    });
                }

            },
            clear: () => {
                setTickSize((1 / Math.pow(10, currencyPair?.quoteCurrencyDecimalPlaces)).toFixed(currencyPair?.quoteCurrencyDecimalPlaces))
                setCurrencyPair(currencyPair)

                setBids([]);
                setAsks([]);
            },
        }),
        [lastPrice]
    );

    // ─── Grouping & derived data ──────────────────────────────────
    const numericTick = parseFloat(tickSize) || 0;

    const groupedAsks = useMemo(
        () => groupByTick(asks, numericTick, "ask", currencyPair).slice(0, depth),
        [asks, numericTick, depth]
    );
    const groupedBids = useMemo(
        () => groupByTick(bids, numericTick, "bid", currencyPair).slice(0, depth),
        [bids, numericTick, depth]
    );

    const asksWithCum = useMemo(() => {
        let cum = Decimal(0);
        return groupedAsks.map((r) => ({ ...r, cum: (cum = cum.add(r.amount)) }));
    }, [groupedAsks]);

    const bidsWithCum = useMemo(() => {
        let cum = Decimal(0);
        return groupedBids.map((r) => ({ ...r, cum: (cum = cum.add(r.amount)) }));
    }, [groupedBids]);

    const maxCum = Decimal.max(
        asksWithCum.at(-1)?.cum ?? new Decimal(1),
        bidsWithCum.at(-1)?.cum ?? new Decimal(1)
    );
    // demo mark price moves a bit


    const priceDir: "up" | "down" = lastPrice >= prevPrice ? "up" : "down";


    const showAsks = layout !== "bid";
    const showBids = layout !== "ask";

    // ─── Row with depth bar ───────────────────────────────────────
    const Row = ({
        entry,
        side,
        progress,
        id,
    }: {
        entry: { price: string; size: string; sum: string };
        side: "bid" | "ask";
        progress: number;
        id: string;
    }) => (
        <Stack
            key={id}
            horizontal
            horizontalAlign="space-between"
            tokens={{ childrenGap: 8 }}
            styles={{ root: { position: "relative" } }}
        >
            {/* depth bar */}
            <Stack
                styles={{
                    root: {
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        left: side === "ask" ? 0 : undefined,
                        right: side === "bid" ? 0 : undefined,
                        width: `${progress}%`,
                        backgroundColor: side === "bid" ? "rgba(0,128,0,0.2)" : "rgba(255,0,0,0.2)",
                    },
                }}
            />
            {/* text */}
            <Text style={{ color: side === "bid" ? "green" : "red" }}>{entry.price}</Text>
            <Text>{entry.size}</Text>
            <Text>{entry.sum}</Text>
        </Stack>
    );

    // ─── JSX ───────────────────────────────────────────────────────
    return (
        <Stack tokens={{ childrenGap: 8 }}>
            {/* Header */}
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                <Text variant="mediumPlus">{t("orderBook")}</Text>
            </Stack>

            {/* Controls */}
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                <Stack horizontal tokens={{ childrenGap: 4 }}>
                    <IconButton iconProps={{ iconName: "BulletedList" }} title="Both" checked={layout === "both"} onClick={() => setLayout("both")} />
                    <IconButton iconProps={{ iconName: "SortUp" }} title="Bid" checked={layout === "bid"} onClick={() => setLayout("bid")} />
                    <IconButton iconProps={{ iconName: "SortDown" }} title="Ask" checked={layout === "ask"} onClick={() => setLayout("ask")} />
                </Stack>

                <Dropdown
                    selectedKey={tickSize}
                    onChange={(_, o) => {
                        if (o) {
                            setTickSize(String(o.key))
                        }
                    }}
                    options={generateOptions(currencyPair?.quoteCurrencyDecimalPlaces)}
                />

            </Stack>

            {/* Labels */}
            <Stack horizontal horizontalAlign="space-between" tokens={{ childrenGap: 8 }}>
                <Text>{t("price") + "(" + (currencyPair?.quoteCurrencySymbol ?? "-") + ")"}</Text>
                <Text>{t("amount") + "(" + (currencyPair?.baseCurrencySymbol ?? "-") + ")"}</Text>
                <Text>{t("total") + "(" + (currencyPair?.quoteCurrencySymbol ?? "-") + ")"}</Text>
            </Stack>

            {/* Scrollable list */}
            <Stack styles={{ root: { maxHeight: 300, overflowY: "auto" } }} tokens={{ childrenGap: 4 }}>
                {/* Asks */}
                {showAsks && (
                    <>
                        {/* Actual ask rows */}
                        {asksWithCum.map((r, i) => (
                            <Row
                                key={`ask-${i}`}
                                id={`ask-${i}`}
                                side="ask"
                                progress={(r.cum.dividedBy(maxCum)).mul(100).toNumber()}
                                entry={{
                                    price: toFixedWithoutRounding(r.price.toString(), currencyPair?.quoteCurrencyDecimalPlaces ?? 9),
                                    size: toFixedWithoutRounding(r.amount.toString(), currencyPair?.baseCurrencyDecimalPlaces ?? 9),
                                    sum: toFixedWithoutRounding(r.total.toString(), currencyPair?.quoteCurrencyDecimalPlaces ?? 9),
                                }}
                            />
                        ))}

                        {/* Padding rows (if less than 3) */}
                        {Array.from({ length: Math.max(0, 3 - asksWithCum.length) }).map((_, i) => (
                            <Row
                                key={`ask-pad-${i}`}
                                id={`ask-pad-${i}`}
                                side="ask"
                                progress={0}
                                entry={{
                                    price: "=",
                                    size: "-",
                                    sum: "-"
                                }}
                            />
                        ))}
                    </>
                )}


                {/* Last traded price */}
                <Stack horizontal verticalAlign="center" horizontalAlign="center" tokens={{ childrenGap: 4 }}>
                    <Text variant="xLarge" style={{ color: priceDir === "up" ? "green" : "red" }}>
                        {lastPrice && currencyPair?.quoteCurrencyDecimalPlaces ? toFixedWithoutRounding(lastPrice.toString(), currencyPair?.quoteCurrencyDecimalPlaces ?? 9) : "-"}
                    </Text>
                    <Icon
                        iconName={priceDir === "up" ? "ChevronUpSmall" : "ChevronDownSmall"}
                        styles={{ root: { fontSize: 16, color: priceDir === "up" ? "green" : "red" } }}
                    />
                    {/*<Text variant="small">
                        {markPrice.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </Text>*/}
                </Stack>

                {/* Bids */}
                {showBids && (
                    <>
                        {/* Actual rows */}
                        {bidsWithCum.map((r, i) => (
                            <Row
                                key={`bid-${i}`}
                                id={`bid-${i}`}
                                side="bid"
                                progress={(r.cum.dividedBy(maxCum)).mul(100).toNumber()}
                                entry={{
                                    price: toFixedWithoutRounding(r.price.toString(), currencyPair?.quoteCurrencyDecimalPlaces ?? 9),
                                    size: toFixedWithoutRounding(r.amount.toString(), currencyPair?.baseCurrencyDecimalPlaces ?? 9),
                                    sum: toFixedWithoutRounding(r.total.toString(), currencyPair?.quoteCurrencyDecimalPlaces ?? 9),
                                }}
                            />
                        ))}

                        {/* Padding rows */}
                        {Array.from({ length: Math.max(0, 3 - bidsWithCum.length) }).map((_, i) => (
                            <Row
                                key={`bid-pad-${i}`}
                                id={`bid-pad-${i}`}
                                side="bid"
                                progress={0}
                                entry={{
                                    price: "=",
                                    size: "-",
                                    sum: "-"
                                }}
                            />
                        ))}
                    </>
                )}
            </Stack>
        </Stack>
    );
});

BinanceOrderBook.displayName = "BinanceOrderBook";
export default BinanceOrderBook;
